import { getUserLocale } from 'src/utils';

/**
 * Helper function to safely parse dates in YYYY-MM-DD format (ISO format)
 * Avoids timezone issues by parsing manually instead of using Date constructor
 */
export const parseIsoDateString = (
  dateString: string | null,
): Date | undefined => {
  if (!dateString) return undefined;

  // Parse YYYY-MM-DD format manually to avoid timezone issues
  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const [, year, month, day] = match;
    // Subtract 1 from month to get the correct month index (0-11)
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    // Fixes year 0-99 being interpreted as 1900+year
    date.setFullYear(parseInt(year));
    return date;
  }

  // Fallback to standard parsing
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? undefined : date;
};

export function formatLocaleDate(
  date: Date,
  locale: Intl.LocalesArgument = getUserLocale(),
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  },
): string {
  // Use formatToParts to get individual components
  const parts = new Intl.DateTimeFormat(locale, options).formatToParts(date);

  // Find the year part
  const yearPart = parts.find((part) => part.type === 'year');

  if (yearPart) {
    // Pad the year value with leading zeros to ensure a minimum of 4 digits
    // The padStart ensures a minimum of 4 digits. So year 0026 is displayed
    // as 0026 rather than just 26 which could confuse users for 2026.
    yearPart.value = yearPart.value.padStart(4, '0');
  }

  // Reconstruct the formatted string
  return parts.map((part) => part.value).join('');
}

/**
 * Format a GraphQL Date scalar (ISO YYYY-MM-DD string or legacy display string) for UI display.
 * Returns '' for null/undefined/whitespace. If the value is valid ISO, returns locale-formatted date;
 * otherwise returns the trimmed string (e.g. already display format).
 */
export function formatDateStringForDisplay(
  value: string | null | undefined,
): string {
  if (value == null) return '';
  const trimmed =
    typeof value === 'string' ? value.trim() : String(value).trim();
  if (!trimmed) return '';
  const parsed = parseIsoDateString(trimmed);
  return parsed ? formatLocaleDate(parsed) : trimmed;
}
